import {
    addTopicToChecklist,
    getTopics,
    getTopicsForChecklist,
    IApiAxiosError,
    IClauseChecklist,
    ITopicList,
} from "@/api";
import { TableFilterToolbar } from "@/components/admin/table/TableFilterToolbar";
import { Modal } from "@/components/shared/Modal";
import { Table } from "@/components/shared/table/Table";
import { useFilterModel } from "@/hooks/useFilterModel";
import { handleRowSelection } from "@/utils";
import { Stack, Typography } from "@mui/material";
import { GridColDef, GridRowSelectionModel } from "@mui/x-data-grid-pro";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";
import { useEffect, useMemo, useRef, useState } from "react";

const columns: GridColDef<ITopicList>[] = [
    {
        field: "name",
        headerName: "Topic Name",
        display: "flex",
        maxWidth: 400,
        flex: 1,
        renderCell: ({ row }) => (
            <Typography variant="body2" noWrap>
                {row.name}
            </Typography>
        ),
    },
    {
        field: "clause_count",
        headerName: "# Clauses",
        type: "number",
        display: "flex",
        flex: 1,
    },
];

interface IProps {
    /**
     * Whether the modal is open.
     */
    open: boolean;
    /**
     * Handler to close the modal.
     */
    onClose: () => void;
    /**
     * The clause checklist to add topics to.
     */
    clauseChecklist: IClauseChecklist;
}

/**
 * A modal for adding topics to a clause checklist.
 */
export const AddTopicsToChecklistDialog = ({
    open,
    onClose,
    clauseChecklist,
}: IProps) => {
    const [selectedTopicIds, setSelectedTopicIds] = useState<number[]>([]);
    const [rows, setRows] = useState<ITopicList[]>([]);

    const { filterModel, handleFilterModelChange, resetFilterModel } =
        useFilterModel();

    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 10,
    });

    const queryClient = useQueryClient();

    // Fetch already assigned topics to exclude them
    const { data: assignedTopics = [] } = useQuery({
        queryKey: ["admin-clause-checklist-topics", clauseChecklist.id],
        queryFn: () => getTopicsForChecklist(clauseChecklist.id),
    });

    const assignedTopicIds = useMemo(
        () => new Set(assignedTopics.map((t) => t.id)),
        [assignedTopics]
    );

    const {
        mutate,
        isPending: mutationPending,
        error: mutationError,
        reset,
    } = useMutation({
        mutationFn: async () => {
            // Add topics in parallel and track results
            const results = await Promise.allSettled(
                selectedTopicIds.map((topicId) =>
                    addTopicToChecklist(clauseChecklist.id, {
                        topic_id: topicId,
                    })
                )
            );

            const failures = results.filter((r) => r.status === "rejected");
            if (failures.length > 0) {
                throw new Error(
                    `Failed to add ${failures.length} of ${selectedTopicIds.length} topics`
                );
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ["admin-clause-checklist-topics", clauseChecklist.id],
            });
            queryClient.invalidateQueries({
                queryKey: [
                    "admin-clause-checklist-details",
                    String(clauseChecklist.id),
                ],
            });
            queryClient.invalidateQueries({
                queryKey: [
                    "admin-clause-checklist-topics-with-clauses",
                    clauseChecklist.id,
                ],
            });
            queryClient.invalidateQueries({
                queryKey: [
                    "clause-checklist-relevance-status",
                    clauseChecklist.id,
                ],
            });
            const count = selectedTopicIds.length;
            enqueueSnackbar(
                `${count} topic${count === 1 ? " has" : "s have"} been successfully added to the checklist.`,
                { variant: "success" }
            );
            handleClose();
        },
    });

    // Fetch topics from the API
    const { data, isFetching } = useQuery({
        queryKey: [
            "admin-clause-checklist-add-topics",
            paginationModel.page,
            paginationModel.pageSize,
            filterModel,
        ],
        queryFn: () =>
            getTopics(
                paginationModel.page + 1,
                paginationModel.pageSize,
                filterModel
            ),
    });

    useEffect(() => {
        if (data) {
            // Filter out already assigned topics
            const filteredItems = data.items.filter(
                (topic) => !assignedTopicIds.has(topic.id)
            );
            setRows(filteredItems);
        }
    }, [data, assignedTopicIds]);

    // Memoize rowCount to avoid resetting the page to 0 when the data is loading
    const rowCountRef = useRef(data?.total || 0);

    const rowCount = useMemo(() => {
        if (data?.total !== undefined) {
            // Adjust for filtered out topics (approximate)
            const adjustedTotal = Math.max(
                0,
                data.total - assignedTopicIds.size
            );
            rowCountRef.current = adjustedTotal;
        }
        return rowCountRef.current;
    }, [data?.total, assignedTopicIds.size]);

    const handleRowSelectionModelChange = (
        rowSelectionModel: GridRowSelectionModel
    ) => {
        setSelectedTopicIds(handleRowSelection(rowSelectionModel, rows));
    };

    const handleClose = () => {
        onClose();
        setSelectedTopicIds([]);
        reset();
        resetFilterModel();
        setPaginationModel({ page: 0, pageSize: 10 });
    };

    const axiosError = mutationError as IApiAxiosError;

    return (
        <Modal
            open={open}
            onClose={handleClose}
            title="Add Topics to Checklist"
            subtitle="Select topics to add to the checklist."
            maxWidth="md"
            isLoading={mutationPending}
            actions={[
                {
                    label: `Add ${selectedTopicIds.length} Topic${selectedTopicIds.length === 1 ? "" : "s"}`,
                    onClick: () => mutate(),
                    variant: "contained",
                    color: "primary",
                    disabled: selectedTopicIds.length === 0 || mutationPending,
                },
            ]}
            footerText={
                axiosError && (
                    <Typography
                        variant="caption"
                        color="error"
                        sx={{ fontSize: "0.75rem" }}
                    >
                        {axiosError.response?.data?.detail ||
                            axiosError.message ||
                            "Failed to add topics to the checklist."}
                    </Typography>
                )
            }
        >
            <Stack height={400}>
                <Table
                    loading={isFetching}
                    columns={columns}
                    rows={rows}
                    checkboxSelection
                    onRowSelectionModelChange={handleRowSelectionModelChange}
                    pageSizeOptions={[10, 25, 50]}
                    paginationModel={paginationModel}
                    rowCount={rowCount}
                    onPaginationModelChange={setPaginationModel}
                    paginationMode="server"
                    filterMode="server"
                    sortingMode="server"
                    onFilterModelChange={handleFilterModelChange}
                    onSortModelChange={handleFilterModelChange}
                    showToolbar
                    slots={{
                        toolbar: TableFilterToolbar,
                    }}
                />
            </Stack>
        </Modal>
    );
};
